/**
 * The FM Manager's own experience (FM Manager rebuild C0.1–C0.4): who gets it,
 * and what their menu and phone bar hold.
 *
 * WHO. `role === 'fm_manager'` (spec C0.1). Unlike the employee shell this is
 * decided by role: the FM Manager's menu is a job description, and a custom
 * role cut from it keeps the base role it widens.
 *
 * WHAT. The prototype's groups — Today · Tickets · Maintenance · Safety ·
 * Vendors · Compliance · Reports · Settings. 🔴 A SLICE NOT YET BUILT NEVER
 * APPEARS AS A BROKEN LINK. Each item points at the screen that does the job
 * today: the new FM screen where its slice has shipped (Home, SLA &
 * escalation — W1; Tickets → /fm/tickets — W2; Spot-checks — W3; Logbooks — W4; PPM planner
 * and Assets → /fm/assets — W5; Permits, Fire drills and Incidents — W6; Vendors, AMC contracts and
 * Invoices — W7; Compliance calendar — W9; Reports and KPI board — W10),
 * otherwise the existing page that already does it. Hybrid work, Wellbeing and IoT appear only
 * when the organisation has the module on (D7); Energy always does.
 *
 * Every item still passes canOfferInNav, so the menu never offers a page the
 * route then refuses.
 */
import type { UserRole } from '@/types';

type RoleUser = { role: UserRole } | null | undefined;

export type FmModule = 'iot' | 'wellbeing' | 'hybrid';
export type FmCountKey = 'tickets' | 'vendors' | 'compliance' | 'ppm_overdue';

export interface FmNavEntry {
  label: string;
  path: string;
  /** Shown only when the organisation has this module on (D7). */
  module?: FmModule;
  /** The count bubble beside it (C0.1), from GET /fm/shell. */
  count?: FmCountKey;
}
export interface FmNavGroup { id: string; heading: string; items: FmNavEntry[] }

export const FM_HOME = '/fm';

export function isFmExperience(user: RoleUser): boolean {
  return user?.role === 'fm_manager';
}

export const FM_NAV_GROUPS: readonly FmNavGroup[] = [
  { id: 'today', heading: 'Today', items: [
    { label: 'Home', path: '/fm' },
    { label: 'Hybrid work', path: '/hybrid', module: 'hybrid' },
  ] },
  { id: 'tickets', heading: 'Tickets', items: [
    { label: 'Tickets', path: '/fm/tickets', count: 'tickets' },
    { label: 'Spot-checks', path: '/fm/spot-checks' },
  ] },
  { id: 'maintenance', heading: 'Maintenance', items: [
    { label: 'Work orders', path: '/maintenance' },
    { label: 'PPM planner', path: '/fm/ppm', count: 'ppm_overdue' },
    { label: 'Assets', path: '/fm/assets' },
    { label: 'Logbooks', path: '/fm/logbooks' },
    { label: 'Scan asset', path: '/scan' },
    { label: 'Checklists', path: '/maintenance/checklists' },
    { label: 'Spaces', path: '/spaces' },
    { label: 'Energy', path: '/energy' },
    { label: 'IoT / Shop floor', path: '/iot', module: 'iot' },
  ] },
  { id: 'safety', heading: 'Safety', items: [
    { label: 'Permits', path: '/fm/permits' },
    { label: 'Fire drills', path: '/fm/drills' },
    { label: 'Incidents', path: '/fm/incidents' },
    { label: 'Safety & emergency', path: '/safety' },
    { label: 'Wellbeing', path: '/wellbeing', module: 'wellbeing' },
  ] },
  // FM Technician C24 / C25 (R1): the FM's technicians — invite, sites,
  // shifts, safety officers, site check-in codes — and the CEA 2023
  // designated-person register.
  { id: 'team', heading: 'Team', items: [
    { label: 'Technicians', path: '/fm/technicians' },
    { label: 'Designated persons', path: '/fm/designated-persons' },
    // FM Technician C27 (R3): the stores' tools and calibration dates, and
    // the lock register (LOTO) the technicians keep.
    { label: 'Tools & instruments', path: '/fm/tools' },
    { label: 'Locks', path: '/fm/locks' },
  ] },
  { id: 'vendors', heading: 'Vendors', items: [
    { label: 'Vendors', path: '/fm/vendors', count: 'vendors' },
    { label: 'AMC contracts', path: '/fm/amc' },
    { label: 'Invoices', path: '/fm/invoices' },
  ] },
  { id: 'compliance', heading: 'Compliance', items: [
    { label: 'Compliance calendar', path: '/fm/compliance', count: 'compliance' },
  ] },
  { id: 'reports', heading: 'Reports', items: [
    { label: 'Reports', path: '/fm/reports' },
    { label: 'KPI board', path: '/fm/kpis' },
    { label: 'Analytics', path: '/analytics' },
  ] },
  { id: 'settings', heading: 'Settings', items: [
    { label: 'SLA & escalation', path: '/fm/settings' },
    // FM Technician C26: what a technician must show to finish, failure codes.
    { label: 'Proof & failure codes', path: '/fm/settings/proof' },
  ] },
];

/** Every route the FM menu can offer. */
export const FM_NAV: readonly string[] = FM_NAV_GROUPS.flatMap((g) => g.items.map((i) => i.path));

/** The groups with the module rule applied (`modules` from GET /fm/shell;
 *  until it loads, a module item is hidden rather than shown and withdrawn). */
export function fmNavGroups(modules: Partial<Record<FmModule, boolean>> | undefined,
                            canOffer: (path: string) => boolean): FmNavGroup[] {
  return FM_NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) =>
      (!i.module || modules?.[i.module] === true) && canOffer(i.path)) }))
    .filter((g) => g.items.length > 0);
}

/** The menu item a pathname belongs to: the longest matching path, so
 *  /fm/settings lights "SLA & escalation", not Home. */
export function fmActivePath(pathname: string): string | null {
  let best: string | null = null;
  for (const p of FM_NAV) {
    if (pathname === p || pathname.startsWith(p + '/')) {
      if (!best || p.length > best.length) best = p;
    }
  }
  return best;
}
